import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthenticationService } from '@services/authentication.service';

import { Role } from '@core/models/role';
import { AuthToken } from '@core/models/token';
import { PermissionsService } from '@services/permissions.service';
import { UserService } from '@services/user.service';
import { ResponsiveService } from '@services/responsive.service';

interface MenuItem {
  label: string;
  icon: string;
  children?: SubMenuItem[];
}

interface SubMenuItem extends MenuItem {
  route?: string;
  action?: () => void;
}

@Component({
  selector: 'novus-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.css'],
  standalone: false
})
export class UserMenuComponent {

  token?: AuthToken;

  menu: MenuItem[] = [];

  impersonatingEmail = '';

  private permissions = inject(PermissionsService);
  private destroyRef = inject(DestroyRef);
  protected authenticationService = inject(AuthenticationService);
  protected userService = inject(UserService);
  protected mobile$ = inject(ResponsiveService).isMobileEvent;

  constructor() {
    this.authenticationService.user.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(token => {
      this.token = token;
      this.buildMenu();
    });

    this.userService.impersonatedUser$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(user => {
      this.impersonatingEmail = user?.email ?? '';
    });
  }

  private buildMenu() {
    this.menu = [];

    const userItems = [];

    if (this.isPersonalVisible) userItems.push({ label: $localize`:Personal settings menu button@@personal:Personal`, icon: 'user', route: '/settings/user/personal' });
    if (this.isSecurityVisible) userItems.push({ label: $localize`:Security menu button@@security:Security`, icon: 'user-shield', route: '/settings/user/security' });
    if (this.isSerialKeysVisible) userItems.push({ label: $localize`:Serial keys menu button@@serialKeys:Serial keys`, icon: 'key', route: '/settings/user/serial-keys' });
    if (this.isLogoutVisible) userItems.push({ label: $localize`:Log out menu button@@logOut:Log out`, icon: 'power-off', action: () => this.logout() });

    this.menu.push({ label: $localize`:Account menu button@@accountMenuButton:Account`, icon: 'user-cog', children: userItems });

    const adminItems = [];

    if (this.isDashboardVisible) adminItems.push({ label: $localize`:@@dashboardButton:Dashboard`, icon: 'gauge', route: '/admin/dashboard' });
    if (this.isUserManagementVisible) adminItems.push({ label: $localize`:@@userManagementButton:User management`, icon: 'users', route: '/admin/users' });
    if (this.isProductManagementVisible) adminItems.push({ label: $localize`:@@productManagementButton:Product management`, icon: 'box-archive', route: '/admin/product-management' });
    if (this.isConfigurationVisible) adminItems.push({ label: $localize`:@@configurationButton:Configuration`, icon: 'gear', route: '/admin/configuration' });
    if (this.isConfigurationVisible) adminItems.push({ label: $localize`:@@gnssBaseStationsButton:GNSS Base Stations`, icon: 'location-dot', route: '/admin/gnss-stations' });
    if (this.isSystemLogsVisible) adminItems.push({ label: $localize`:@@systemLogsButton:System logs`, icon: 'clipboard', route: '/admin/system-logs' });

    if (adminItems.length > 0) {
      this.menu.push(
        { label: $localize`:Admin settings menu button@@adminSettings:Admin settings`, icon: 'building-shield', children: adminItems },
      );
    }
  }

  logout() {
    this.authenticationService.logout();
  }

  returnFromImpersonation() {
    this.authenticationService.returnFromImpersonation()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: () => undefined });
  }

  // True returns for completeness
  get isPersonalVisible() { return true; }

  get isSecurityVisible() { return true; }

  get isSerialKeysVisible() {
    return this.permissions.currentUserIsAdmin();
  }

  get isLogoutVisible() { return true; }


  get isConfigurationVisible() {
    return this.permissions.currentUserIsAdmin();
  }

  get isProductManagementVisible() {
    return this.token?.role == Role.Admin;
  }

  get isSystemLogsVisible() {
    return this.token?.role == Role.Admin || this.token?.permissions.systemStatus.viewLogs;
  }

  get isUserManagementVisible() {
    return this.token?.role == Role.Admin ||
      Object.values(this.token?.permissions.userManagement || {}).some(t => t) ||
      Object.values(this.token?.permissions.userGroup || {}).some(t => t);
  }

  get isDashboardVisible() {
    return this.token?.role == Role.Admin || this.token?.permissions.systemStatus.viewServerStatus;
  }

}
